import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ListDashes, MagnifyingGlass, DownloadSimple, Eye, ArrowClockwise, User,
  ChartBar, SquaresFour,
} from "@phosphor-icons/react";
import DateRangePicker, { todayRange, lastNDaysRange, rangeToParams } from "@/components/DateRangePicker";
import { formatReportTime, resolveTimeZone } from "@/lib/timezone";

// Default: last 2 account-TZ days so UTC+ browsers never miss "today's" UTC calls.
// cache-bust: 2026-10-27T22:08Z
const defaultCdrRange = (tz) => lastNDaysRange(tz || "UTC", 2);
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, Legend,
} from "recharts";

const PAGE_SIZE = 500;
const EMPTY_FILTERS = {
  q: "", customer: "", reseller: "", ai_engine: "", campaign: "",
  transfer_result: "", disposition: "", amd_result: "",
};

const CHART_COLORS = ["#0d9488", "#0284c7", "#059669", "#e11d48", "#d97706", "#64748b", "#0891b2", "#b45309"];
const tooltipStyle = {
  background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))",
  borderRadius: 10, fontSize: 12, color: "hsl(var(--popover-foreground))",
};
const axis = { stroke: "hsl(var(--muted-foreground))", fontSize: 11 };

const buildFilterParams = (f, range) => {
  const p = {};
  Object.entries(f).forEach(([k, v]) => { if (v) p[k] = v; });
  return { ...p, ...rangeToParams(range) };
};
const TRANSFER_LABELS = {
  call_follow: "Call Follow",
  inbound_group: "Inbound group",
  ingroup: "Inbound group",
  direct_agent: "Direct agent",
  external_sip: "External SIP",
  voicemail: "Voicemail Dropped",
  transfer_succeeded: "Transfer succeeded",
  transfer_failed: "Transfer failed",
  not_attempted: "Not attempted",
};
const transferLabel = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  return TRANSFER_LABELS[String(v).toLowerCase()] || v;
};
/** Inbound DID left in an agent's mailbox (not AMD campaign voicemail_left). */
const isVoicemailDropped = (r) => {
  if (!r) return false;
  if (String(r.transfer_result || "").toLowerCase() === "voicemail") return true;
  const d = String(r.disposition || "").toLowerCase();
  const h = String(r.hangup_cause || "").toLowerCase();
  return d === "voicemail" && (h === "voicemail" || h === "");
};
const voicemailDroppedName = (r) => (
  (r?.agent_name || "").trim()
  || (r?.voicemail_mailbox || "").trim()
  || null
);
const campaignLabel = (r) => {
  const name = (r?.campaign_name || "").trim();
  if (!name) return "—";
  const prefix = (r?.campaign_prefix || "").trim();
  return prefix ? `${prefix} / ${name}` : name;
};
const nfmt = (v) => Number(v || 0).toLocaleString();
const nn = (v) => (v === null || v === undefined || v === "" ? "—" : v);
const dur = (s) => (s === null || s === undefined ? "—" : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`);
const humanize = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
/** Prefer API display label; else ``Name · host`` when both differ. */
const serverDisplay = (display, name, host) => {
  if (display) return display;
  const n = (name || "").trim();
  const h = (host || "").trim();
  if (n && h && n !== h) return `${n} · ${h}`;
  return n || h || null;
};

function CdrRecordingPlayer({ row }) {
  const [url, setUrl] = useState(null);
  const [state, setState] = useState(row?.has_recording ? "idle" : "none");

  useEffect(() => {
    setUrl((prev) => {
      if (prev) window.URL.revokeObjectURL(prev);
      return null;
    });
    setState(row?.has_recording ? "idle" : "none");
  }, [row?.id, row?.has_recording]);

  useEffect(() => () => {
    if (url) window.URL.revokeObjectURL(url);
  }, [url]);

  const load = async () => {
    if (!row?.id || state === "loading" || url) return;
    setState("loading");
    try {
      const res = await api.get(`/cdr/${row.id}/audio`, { responseType: "blob" });
      setUrl(window.URL.createObjectURL(res.data));
      setState("ready");
    } catch {
      setState("none");
    }
  };

  if (state === "none") {
    return <span className="text-muted-foreground text-xs">—</span>;
  }
  if (state === "ready" && url) {
    return (
      <audio
        controls
        preload="none"
        src={url}
        className="h-8 w-[11rem] max-w-full"
        data-testid={`cdr-audio-${row.id}`}
      />
    );
  }
  if (state === "loading") {
    return <span className="text-xs text-muted-foreground">Loading…</span>;
  }
  return (
    <button
      type="button"
      className="inline-flex items-center rounded-md border border-border px-2 py-1 text-xs font-medium hover:bg-muted"
      onClick={load}
      data-testid={`cdr-play-${row.id}`}
    >
      Play
    </button>
  );
}

/** True when Collect Customer Info captured at least one non-empty value. */
const hasCollectedData = (r) => {
  const info = r?.customer_info;
  if (!info || typeof info !== "object") return false;
  return Object.values(info).some((item) => {
    const v = item && typeof item === "object" ? item.value : item;
    return v != null && String(v).trim() !== "";
  });
};

const DISP_FAIL = new Set([
  "no_response", "transfer_failed", "caller_hangup", "rejected_prefix",
  "no_runnable_flow", "engine_error", "no_audio", "failed", "error",
  "campaign stopped", "campaign not active", "campaign_stopped", "campaign_inactive",
  "abandoned", "abandon", "noanswer", "no_answer", "no answer",
]);
const DISP_LABELS = {
  abandoned: "Abandoned",
  abandon: "Abandoned",
  "missed call": "Missed Call",
  missed_call: "Missed Call",
  missed: "Missed Call",
  dead_end: "Incomplete Flow (no next step)",
  call_timeout: "Call Timeout",
  max_call_duration: "Call Timeout",
  "campaign stopped": "Campaign stopped",
  campaign_stopped: "Campaign stopped",
  "campaign not active": "Campaign not active",
  campaign_inactive: "Campaign not active",
  noanswer: "Not Answered",
  no_answer: "Not Answered",
  "no answer": "Not Answered",
  voicemail: "Voicemail Dropped",
};
const dispKey = (d) => String(d || "").toLowerCase().replace(/_/g, " ").trim();
const isMissedCall = (d) => {
  const k = dispKey(d);
  return k === "missed call" || k === "missed";
};
const dispLabel = (d) => DISP_LABELS[String(d || "").toLowerCase()] || DISP_LABELS[dispKey(d)] || humanize(d);
const hangupSideLabel = (r) => {
  if (!r) return null;
  if (r.disconnected_by_display) return r.disconnected_by_display;
  const d = String(r.disposition || "").toLowerCase();
  const cause = String(r.hangup_cause || "").toLowerCase();
  const by = String(r.disconnected_by || "").toLowerCase();
  if (by === "operator" || cause === "operator_disconnect") {
    return r.disconnected_by_name || "Customer";
  }
  if (by === "agent" || cause === "agent_hangup" || d === "agent_hangup") return "Agent";
  if (by === "caller" || cause === "customer_left" || cause === "customer_disconnected"
    || cause === "caller_hangup" || d === "caller_hangup" || d === "hangup") return "Caller";
  return null;
};
const systemDisposition = (r, disp) => {
  const raw = disp || r?.disposition;
  const party = hangupSideLabel(r);
  if (party && String(raw || "").trim() === party) {
    const cause = String(r?.hangup_cause || "").toLowerCase();
    if (["customer_left", "customer_disconnected", "caller_hangup", "remote_hangup"].includes(cause)) {
      return "caller_hangup";
    }
    return null;
  }
  return raw;
};
const dispClass = (d) => {
  if (!d) return "bg-muted text-muted-foreground";
  const k = String(d).toLowerCase();
  if (isMissedCall(d)) return "bg-red-600 text-white border border-red-700";
  if (k === "transfer_succeeded" || k === "completed") return "bg-emerald-100 text-emerald-700 border border-emerald-200";
  if (DISP_FAIL.has(k)) return "bg-rose-100 text-rose-700 border border-rose-200";
  return "bg-amber-100 text-amber-700 border border-amber-200";
};

const amdClass = (v) => {
  const k = String(v || "").toLowerCase();
  if (k === "human") return "bg-emerald-100 text-emerald-700 border border-emerald-200";
  if (k === "machine") return "bg-rose-100 text-rose-700 border border-rose-200";
  if (k === "notsure") return "bg-amber-100 text-amber-700 border border-amber-200";
  return "bg-muted text-muted-foreground";
};
const amdLabel = (v, action, layer) => {
  if (String(action || "").toLowerCase() === "voicemail_left") return "Voicemail Left";
  const base = { human: "Human", machine: "Voicemail", notsure: "Not sure",
    disabled: "Off", error: "Err" }[String(v || "").toLowerCase()] || null;
  if (base === "Voicemail" && (layer === 1 || layer === 2 || layer === "1" || layer === "2")) {
    return `Voicemail:${layer}`;
  }
  return base;
};
const amdResultPlain = (v, action) => {
  if (String(action || "").toLowerCase() === "voicemail_left") return "Voicemail Left";
  return { human: "Human", machine: "Voicemail", notsure: "Not sure",
    disabled: "Off", error: "Err" }[String(v || "").toLowerCase()] || null;
};
const hasAmdLayer = (d) => {
  const n = Number(d?.amd_layer);
  return n === 1 || n === 2;
};
const amdDetectionLabel = (layer) => {
  const n = Number(layer);
  if (n === 1) return "Layer 1 · FreeSWITCH";
  if (n === 2) return "Layer 2 · AI Engine";
  return null;
};
const amdL1Pair = (result, cause) => {
  const r = String(result || "").trim().toUpperCase();
  const c = String(cause || "").trim().toUpperCase();
  if (r && c) return `${r} / ${c}`;
  return r || c || null;
};
const amdCauseLabel = (cause) => {
  const c = String(cause || "").trim();
  return c ? c.toUpperCase() : null;
};

/**
 * @param {{ channel?: "direct" | "reseller" | null, direction?: "outbound" | "inbound" | null }} props
 * Admin /cdr/outbound → direct outbound; /cdr/inbound → inbound; /cdr/reseller → reseller.
 * Reseller/customer roles ignore channel (tenant scope already applies).
 */
export default function CDR({ channel = null, direction = null }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isReseller = user?.role === "reseller";
  const isCustomer = user?.role === "customer";
  // Admin default CDR is direct-only; Reseller CDR page passes channel="reseller".
  const effectiveChannel = isAdmin ? (channel === "reseller" ? "reseller" : "direct") : null;
  const isResellerChannel = effectiveChannel === "reseller";
  const callDirection = direction === "inbound" || direction === "outbound" ? direction : null;
  const pageSize = PAGE_SIZE;
  const rowDisp = (r) => ((isReseller || isAdmin) && r.reseller_disposition) ? r.reseller_disposition : r.disposition;
  const [listTimezone, setListTimezone] = useState(user?.timezone || "UTC");
  const accountTz = resolveTimeZone(listTimezone || user?.timezone || "UTC");
  const fmt = (iso, display) => formatReportTime(iso, accountTz, display);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  // Range days are account System Settings calendar days (backend filter TZ),
  // not the browser's local day — otherwise UTC+ users see an empty CDR list.
  const [range, setRange] = useState(() => defaultCdrRange(user?.timezone));
  const [appliedRange, setAppliedRange] = useState(() => defaultCdrRange(user?.timezone));
  const [engines, setEngines] = useState([]);
  const [details, setDetails] = useState(null);
  const [infoModal, setInfoModal] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [stats, setStats] = useState(null);
  const [dispOptions, setDispOptions] = useState([]);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioState, setAudioState] = useState("idle");
  const [greetingText, setGreetingText] = useState("");
  const [greetingState, setGreetingState] = useState("idle");
  const [view, setView] = useState("table"); // table | chart
  const [fetchTick, setFetchTick] = useState(0);
  const abortRef = useRef(null);

  const channelParams = useMemo(() => {
    const p = {};
    if (effectiveChannel) p.channel = effectiveChannel;
    if (callDirection) p.direction = callDirection;
    return p;
  }, [effectiveChannel, callDirection]);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    const filterParams = { ...buildFilterParams(applied, appliedRange), ...channelParams };
    try {
      const [listRes, statsRes] = await Promise.all([
        api.get("/cdr", { params: { page, page_size: pageSize, ...filterParams }, signal: ac.signal }),
        api.get("/cdr/stats", { params: filterParams, signal: ac.signal }),
      ]);
      setRows(listRes.data.items || []);
      setTotal(listRes.data.total || 0);
      if (listRes.data.timezone) setListTimezone(listRes.data.timezone);
      setStats(statsRes.data);
    } catch (e) {
      if (e.code === "ERR_CANCELED" || e.name === "CanceledError" || e.name === "AbortError") return;
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      if (!ac.signal.aborted) setLoading(false);
    }
  }, [page, pageSize, applied, appliedRange, channelParams]);

  useEffect(() => { load(); }, [load, fetchTick]);

  useEffect(() => {
    if (!details?.id) { setAudioUrl(null); setAudioState("idle"); return; }
    let cancelled = false; let objUrl = null;
    setAudioState("loading"); setAudioUrl(null);
    api.get(`/cdr/${details.id}/audio`, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        objUrl = window.URL.createObjectURL(res.data);
        setAudioUrl(objUrl); setAudioState("ready");
      })
      .catch((e) => {
        if (cancelled) return;
        setAudioState(e.response?.status === 404 ? "none" : "error");
      });
    return () => { cancelled = true; if (objUrl) window.URL.revokeObjectURL(objUrl); };
  }, [details?.id]);

  useEffect(() => {
    const cdrId = details?.id;
    const layer = Number(details?.amd_layer);
    if (!cdrId || (layer !== 1 && layer !== 2)) {
      setGreetingText("");
      setGreetingState("idle");
      return;
    }
    let cancelled = false;
    setGreetingState("loading");
    setGreetingText("");
    api.get(`/cdr/${cdrId}/greeting`)
      .then((res) => {
        if (cancelled) return;
        const text = String(res.data?.text || "").trim();
        if (text) {
          setGreetingText(text);
          setGreetingState("ready");
        } else {
          setGreetingState("none");
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setGreetingState(e.response?.status === 404 ? "none" : "error");
      });
    return () => { cancelled = true; };
  }, [details?.id, details?.amd_layer]);

  useEffect(() => {
    api.get("/ai-engines/overview").then(({ data }) => setEngines(data.engines || [])).catch(() => {});
    api.get("/cdr/stats", { params: channelParams })
      .then(({ data }) => setDispOptions(Object.keys(data.by_disposition || {}))).catch(() => {});
  }, [channelParams]);

  const prevChannel = useRef(effectiveChannel);
  // Reset paging/filters when switching Direct ↔ Reseller CDR.
  useEffect(() => {
    if (prevChannel.current === effectiveChannel) return;
    prevChannel.current = effectiveChannel;
    setFilters({ ...EMPTY_FILTERS });
    setApplied({ ...EMPTY_FILTERS });
    setRange(defaultCdrRange(accountTz));
    setAppliedRange(defaultCdrRange(accountTz));
    setPage(1);
    setView("table");
    setFetchTick((n) => n + 1);
  }, [effectiveChannel, accountTz]);

  const engineOptions = engines.length ? engines.map((e) => ({ id: e.engine_id, name: e.name }))
    : [...new Map(rows.filter((r) => r.ai_engine_id)
        .map((r) => [r.ai_engine_id, { id: r.ai_engine_id, name: r.ai_engine_name || r.ai_engine_id }])).values()];

  const apply = () => {
    setPage(1);
    setApplied({ ...filters });
    const next = range?.from
      ? { from: range.from, to: range.to || range.from }
      : defaultCdrRange(accountTz);
    setRange(next);
    setAppliedRange(next);
    setFetchTick((n) => n + 1);
  };
  const reset = () => {
    setFilters({ ...EMPTY_FILTERS });
    setApplied({ ...EMPTY_FILTERS });
    setRange(defaultCdrRange(accountTz));
    setAppliedRange(defaultCdrRange(accountTz));
    setPage(1);
    setFetchTick((n) => n + 1);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = { ...buildFilterParams(applied, appliedRange), ...channelParams };
      const res = await api.get("/cdr/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = isResellerChannel ? "reseller-cdrs.csv" : "cdrs.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setExporting(false); }
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const summary = stats?.summary || {};
  const chartData = useMemo(() => {
    const entries = Object.entries(stats?.by_disposition || {})
      .sort((a, b) => b[1] - a[1]);
    const top = entries.slice(0, 8);
    const rest = entries.slice(8);
    const other = rest.reduce((n, [, v]) => n + v, 0);
    const rowsChart = top.map(([k, v]) => ({ name: dispLabel(k), key: k, count: v }));
    if (other > 0) rowsChart.push({ name: "Other", key: "other", count: other });
    return rowsChart;
  }, [stats]);
  const amdLayerChart = useMemo(() => {
    const l1 = stats?.by_amd_layer?.["1"] || {};
    const l2 = stats?.by_amd_layer?.["2"] || {};
    return [
      { name: "Voicemail", layer1: l1.machine || 0, layer2: l2.machine || 0 },
      { name: "Human", layer1: l1.human || 0, layer2: l2.human || 0 },
      { name: "Not sure", layer1: l1.notsure || 0, layer2: l2.notsure || 0 },
    ].filter((r) => r.layer1 || r.layer2);
  }, [stats]);

  const title = isResellerChannel
    ? "Reseller CDR"
    : callDirection === "inbound"
      ? "Inbound CDR"
      : callDirection === "outbound"
        ? "Outbound CDR"
        : "Call Detail Records";
  const subtitle = `${
    isResellerChannel
      ? "CDRs for customers under resellers — outcomes, AI agent duration, and AI charges."
      : callDirection === "inbound"
        ? "Inbound calls only (DID / inbound group) — phone, campaign, disposition, collected data."
        : callDirection === "outbound"
          ? "Outbound dials only — phone, campaign, disposition, collected data."
          : isAdmin && effectiveChannel === "direct"
            ? "Direct-customer calls only (Lead ID, phone, campaign, disposition, AI charges)."
            : isCustomer
              ? "Per-call outcomes for your campaigns (phone, campaign, disposition, collected data)."
              : "Per-call VICIdial metadata and outcome for AI-engine calls."
  } Times in ${accountTz}.`;

  const kpiCards = [
    { key: "total", label: "Total Calls", value: nfmt(stats?.total ?? 0), testid: "cdr-stat-total" },
    { key: "xfer_ok", label: "Transfer OK", value: nfmt(summary.transfer_succeeded ?? 0), testid: "cdr-stat-xfer-ok" },
    { key: "xfer_fail", label: "Transfer Failed", value: nfmt(summary.transfer_failed ?? 0), testid: "cdr-stat-xfer-fail" },
    { key: "hangup", label: "Caller Hangup", value: nfmt(summary.caller_hangup ?? 0), testid: "cdr-stat-hangup" },
    {
      key: "amd",
      label: "AMD Detected",
      value: nfmt(summary.amd_detected ?? 0),
      sub: `L1: ${nfmt(summary.amd_l1 ?? 0)} · L2: ${nfmt(summary.amd_l2 ?? 0)}`,
      testid: "cdr-stat-amd",
    },
    {
      key: "ai_charge",
      label: "AI Charges",
      value: money(summary.ai_charge_minor ?? 0),
      testid: "cdr-stat-ai-charges",
      money: true,
    },
  ];

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle}>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-border p-0.5 bg-muted/40" data-testid="cdr-view-toggle">
            <Button
              size="sm" variant={view === "table" ? "secondary" : "ghost"}
              className="rounded-md h-8 px-2.5"
              onClick={() => setView("table")}
              data-testid="cdr-view-table"
            >
              <SquaresFour className="w-4 h-4 mr-1" /> Table
            </Button>
            <Button
              size="sm" variant={view === "chart" ? "secondary" : "ghost"}
              className="rounded-md h-8 px-2.5"
              onClick={() => setView("chart")}
              data-testid="cdr-view-chart"
            >
              <ChartBar className="w-4 h-4 mr-1" /> Chart
            </Button>
          </div>
          <Button variant="outline" onClick={exportCsv} disabled={exporting} data-testid="cdr-export-btn" className="rounded-lg">
            <DownloadSimple className="w-4 h-4 mr-1.5" weight="bold" /> {exporting ? "Exporting…" : "Export CSV"}
          </Button>
        </div>
      </PageHeader>

      {/* Compact KPI cards — fixed set, not one-per-disposition */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 mb-5" data-testid="cdr-stats-cards">
          {kpiCards.map((c) => (
            <div key={c.key} className="rounded-xl border border-border bg-card card-shadow p-4" data-testid={c.testid}>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{c.label}</p>
              <p className={`font-semibold tabular-nums mt-1 ${c.money ? "text-xl" : "text-2xl"}`}>{c.value}</p>
              {c.sub ? <p className="text-[11px] text-muted-foreground mt-1 tabular-nums" data-testid={`${c.testid}-layers`}>{c.sub}</p> : null}
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="rounded-xl border border-border bg-card card-shadow p-4 mb-5 grid gap-3 md:grid-cols-6" data-testid="cdr-filters">
        <div className="space-y-1 md:col-span-2">
          <Label className="text-xs">{isCustomer ? "Search (phone)" : "Search (phone / lead / unique id)"}</Label>
          <Input data-testid="cdr-search" value={filters.q} placeholder="e.g. 1005551234"
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && apply()} />
        </div>
        {!isCustomer && (
          <div className="space-y-1">
            <Label className="text-xs">AI Engine</Label>
            <select data-testid="cdr-filter-engine" className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={filters.ai_engine} onChange={(e) => setFilters({ ...filters, ai_engine: e.target.value })}>
              <option value="">All</option>
              {engineOptions.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
        )}
        <div className="space-y-1">
          <Label className="text-xs">Campaign</Label>
          <Input data-testid="cdr-filter-campaign" value={filters.campaign} placeholder="id / prefix"
            onChange={(e) => setFilters({ ...filters, campaign: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Transfer result</Label>
          <select data-testid="cdr-filter-transfer" className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={filters.transfer_result} onChange={(e) => setFilters({ ...filters, transfer_result: e.target.value })}>
            <option value="">All</option>
            <option value="transfer_succeeded">Transfer succeeded</option>
            <option value="transfer_failed">Transfer failed</option>
            <option value="not_attempted">Not attempted</option>
            <option value="call_follow">Call Follow</option>
            <option value="inbound_group">Inbound group</option>
            <option value="direct_agent">Direct agent</option>
            <option value="external_sip">External SIP</option>
            <option value="voicemail">Voicemail Dropped</option>
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Disposition</Label>
          <select data-testid="cdr-filter-disposition" className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={filters.disposition} onChange={(e) => setFilters({ ...filters, disposition: e.target.value })}>
            <option value="">All</option>
            {dispOptions.map((d) => <option key={d} value={d}>{dispLabel(d)}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">AMD</Label>
          <select data-testid="cdr-filter-amd" className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={filters.amd_result} onChange={(e) => setFilters({ ...filters, amd_result: e.target.value })}>
            <option value="">All</option>
            <option value="human">Human</option>
            <option value="machine">Voicemail</option>
            <option value="notsure">Not sure</option>
            <option value="disabled">Off</option>
            <option value="error">Error</option>
          </select>
        </div>
        <div className="space-y-1 md:col-span-2">
          <Label className="text-xs">Date range</Label>
          <DateRangePicker value={range} onChange={setRange} timeZone={accountTz} testid="cdr-daterange" />
        </div>
        {!isCustomer && (
          <div className="space-y-1">
            <Label className="text-xs">Customer</Label>
            <Input data-testid="cdr-filter-customer" value={filters.customer} placeholder="Customer name"
              onChange={(e) => setFilters({ ...filters, customer: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && apply()} />
          </div>
        )}
        {isResellerChannel && (
          <div className="space-y-1">
            <Label className="text-xs">Reseller</Label>
            <Input data-testid="cdr-filter-reseller" value={filters.reseller} placeholder="Reseller name"
              onChange={(e) => setFilters({ ...filters, reseller: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && apply()} />
          </div>
        )}
        <div className="md:col-span-6 flex gap-2">
          <Button size="sm" className="rounded-lg" onClick={apply} data-testid="cdr-apply-btn">
            <MagnifyingGlass className="w-4 h-4 mr-1.5" /> {loading ? "Searching…" : "Apply"}
          </Button>
          <Button size="sm" variant="ghost" className="rounded-lg" onClick={reset} data-testid="cdr-reset-btn">
            <ArrowClockwise className="w-4 h-4 mr-1.5" /> Reset
          </Button>
          <span className="ml-auto self-center text-xs text-muted-foreground" data-testid="cdr-total">{total} records</span>
        </div>
      </div>

      {view === "chart" ? (
        <div className="space-y-5 mb-5">
          <div className="rounded-xl border border-border bg-card card-shadow p-5" data-testid="cdr-amd-layer-chart">
            <div className="mb-4">
              <h3 className="text-sm font-semibold">AMD detection by layer</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Layer 1 (FreeSWITCH) vs Layer 2 (AI engine) for the current filters</p>
            </div>
            {amdLayerChart.length === 0 ? (
              <EmptyState testid="cdr-amd-layer-empty" icon={ChartBar} title="No AMD layer data"
                description="Layer counts appear after two-layer AMD (VICI Bot) calls complete." />
            ) : (
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={amdLayerChart} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" tick={axis} axisLine={false} tickLine={false} />
                    <YAxis tick={axis} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend />
                    <Bar dataKey="layer1" name="Layer 1 · FreeSWITCH" fill="#38bdf8" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="layer2" name="Layer 2 · AI engine" fill="#a78bfa" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
          <div className="rounded-xl border border-border bg-card card-shadow p-5" data-testid="cdr-chart">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
              <div>
                <h3 className="text-sm font-semibold">Disposition mix</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Top outcomes for the current filters</p>
              </div>
              <div className="text-right" data-testid="cdr-chart-ai-charges">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">AI Charges</p>
                <p className="text-xl font-semibold tabular-nums">{money(summary.ai_charge_minor ?? 0)}</p>
              </div>
            </div>
            {chartData.length === 0 ? (
              <EmptyState testid="cdr-chart-empty" icon={ChartBar} title="No chart data"
                description="Apply filters or wait for calls to complete." />
            ) : (
              <div className="h-[360px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                    <XAxis type="number" tick={axis} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="name" width={140} tick={axis} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => [v, "Calls"]} />
                    <Bar dataKey="count" name="Calls" radius={[0, 4, 4, 0]}>
                      {chartData.map((entry, i) => (
                        <Cell key={entry.key} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      ) : loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState testid="cdr-empty" icon={ListDashes} title="No call records"
          description={isResellerChannel
            ? "Reseller-customer CDRs appear here after their AI-engine calls complete."
            : isCustomer
              ? "CDRs appear here after calls complete. Each record shows the phone, campaign, outcome, and whether customer info was collected."
              : "CDRs appear here after AI-engine calls complete."} />
      ) : (
        <div className="rounded-xl border border-border bg-card card-shadow overflow-x-auto" data-testid="cdr-table">
          <Table>
            <TableHeader><TableRow>
              {!isCustomer && <TableHead>Lead ID</TableHead>}
              <TableHead>Phone</TableHead>
              {!isCustomer && <TableHead>CALLERID</TableHead>}
              {!isCustomer && <TableHead>Client</TableHead>}
              {isResellerChannel && <TableHead>Reseller</TableHead>}
              {!isCustomer && <TableHead>Customer</TableHead>}
              <TableHead>Campaign</TableHead>
              {!isCustomer && <TableHead>Unique ID</TableHead>}
              <TableHead>Start</TableHead>
              <TableHead className="text-right">Dur</TableHead>
              <TableHead className="text-right">AI Agent dur.</TableHead>
              <TableHead className="text-right">AI charge</TableHead>
              {!isCustomer && <TableHead>AI Engine</TableHead>}
              <TableHead>Transfer</TableHead>
              <TableHead>Disposition</TableHead>
              <TableHead>AMD</TableHead>
              {isCustomer && <TableHead>Recording</TableHead>}
              {isCustomer && <TableHead className="text-center">Customer info</TableHead>}
              <TableHead className="text-right">Details</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow
                  key={r.id}
                  className={
                    isMissedCall(rowDisp(r))
                      ? "bg-red-50 hover:bg-red-100/80"
                      : "hover:bg-muted/40"
                  }
                  data-testid={`cdr-row-${r.id}`}
                  data-missed={isMissedCall(rowDisp(r)) ? "true" : undefined}
                >
                  {!isCustomer && (
                    <TableCell className="font-mono text-xs" data-testid={`cdr-lead-${r.id}`}>{nn(r.lead_id)}</TableCell>
                  )}
                  <TableCell className="font-mono text-sm">
                    {r.phone_number ? (
                      <Link
                        to={`/cdr/phone/${encodeURIComponent(r.phone_number)}`}
                        className="text-sky-700 hover:underline"
                        data-testid={`cdr-phone-link-${r.id}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {r.phone_number}
                      </Link>
                    ) : "—"}
                  </TableCell>
                  {!isCustomer && <TableCell className="text-sm">{nn(r.callerid)}</TableCell>}
                  {!isCustomer && <TableCell className="font-mono text-xs">{nn(r.client_id)}</TableCell>}
                  {isResellerChannel && (
                    <TableCell className="text-xs" data-testid={`cdr-reseller-${r.id}`}>{nn(r.reseller_name)}</TableCell>
                  )}
                  {!isCustomer && (
                    <TableCell className="text-xs" data-testid={`cdr-customer-${r.id}`}>{nn(r.customer_name)}</TableCell>
                  )}
                  <TableCell className="text-xs">{campaignLabel(r)}</TableCell>
                  {!isCustomer && (
                    <TableCell className="font-mono text-xs truncate max-w-[130px]">{nn(r.unique_id)}</TableCell>
                  )}
                  <TableCell className="text-xs text-muted-foreground">{fmt(r.start_time, r.start_time_display)}</TableCell>
                  <TableCell className="text-right tabular-nums text-xs">{dur(r.duration_sec)}</TableCell>
                  <TableCell className="text-right tabular-nums text-xs" data-testid={`cdr-agent-dur-${r.id}`}>
                    {r.ai_agent_duration_sec > 0 ? dur(r.ai_agent_duration_sec) : "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-xs" data-testid={`cdr-ai-charge-${r.id}`}>
                    {r.ai_charge_minor > 0 ? money(r.ai_charge_minor) : "—"}
                  </TableCell>
                  {!isCustomer && (
                    <TableCell className="text-xs">{nn(r.ai_engine_name || r.ai_engine_id)}</TableCell>
                  )}
                  <TableCell className="text-xs">{transferLabel(r.transfer_result)}</TableCell>
                  <TableCell className="text-xs">
                    {r.disposition
                      ? <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${dispClass(systemDisposition(r, rowDisp(r)))}`} data-testid={`cdr-disp-${r.id}`}>{dispLabel(systemDisposition(r, rowDisp(r)))}</span>
                      : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-xs">
                    {amdLabel(r.amd_result, r.amd_action, r.amd_layer)
                      ? <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${amdClass(r.amd_result)}`} data-testid={`cdr-amd-${r.id}`} title={r.amd_cause || (r.amd_layer ? `layer ${r.amd_layer}` : "")}>{amdLabel(r.amd_result, r.amd_action, r.amd_layer)}</span>
                      : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  {isCustomer && (
                    <TableCell className="min-w-[11rem]" data-testid={`cdr-recording-${r.id}`}>
                      <CdrRecordingPlayer row={r} />
                    </TableCell>
                  )}
                  {isCustomer && (
                    <TableCell className="text-center" data-testid={`cdr-data-${r.id}`}>
                      {hasCollectedData(r) ? (
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-full p-1.5 text-teal-700 hover:bg-teal-50"
                          title="View collected customer info"
                          onClick={() => setInfoModal(r)}
                          data-testid={`cdr-data-icon-${r.id}`}
                        >
                          <User className="w-4 h-4" weight="fill" />
                        </button>
                      ) : null}
                    </TableCell>
                  )}
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" className="rounded-lg px-2" onClick={() => setDetails(r)} data-testid={`cdr-details-${r.id}`}><Eye className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {view === "table" && total > pageSize && (
        <div className="flex items-center justify-between mt-4 text-sm" data-testid="cdr-pagination">
          <span className="text-muted-foreground">Page {page} of {pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-lg" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="cdr-prev">Prev</Button>
            <Button variant="outline" size="sm" className="rounded-lg" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="cdr-next">Next</Button>
          </div>
        </div>
      )}

      <Dialog open={!!infoModal} onOpenChange={(v) => !v && setInfoModal(null)}>
        <DialogContent data-testid="cdr-customer-info-dialog">
          <DialogHeader>
            <DialogTitle>Customer info</DialogTitle>
            <DialogDescription>
              Collected during call {nn(infoModal?.phone_number)}
              {infoModal?.start_time ? ` · ${fmt(infoModal.start_time, infoModal.start_time_display)}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {Object.entries(infoModal?.customer_info || {}).map(([key, item]) => {
              const row = item && typeof item === "object" ? item : { value: item };
              return (
                <div key={key} data-testid={`cdr-info-modal-${key}`}>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">{row.label || key}</p>
                  <p className="text-sm break-all">{row.value || "—"}</p>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!details} onOpenChange={(v) => !v && setDetails(null)}>
        <DialogContent data-testid="cdr-details-dialog">
          <DialogHeader><DialogTitle>Call Detail</DialogTitle>
            <DialogDescription>
              {isCustomer
                ? `Phone ${nn(details?.phone_number)}`
                : `Unique ID ${nn(details?.unique_id)}`}
            </DialogDescription></DialogHeader>
          <div className="py-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {[
              ...(!isCustomer ? [["Lead ID", details?.lead_id]] : []),
              ["Phone (Caller-Id)", details?.phone_number],
              ...(!isCustomer ? [["CALLERID (value)", details?.callerid]] : []),
              ...(!isCustomer ? [["Client ID", details?.client_id]] : []),
              ...(isResellerChannel ? [["Reseller", details?.reseller_name]] : []),
              ...(!isCustomer ? [["Customer", details?.customer_name]] : []),
              ["Campaign", details ? campaignLabel(details) : null],
              ...(!isCustomer ? [["Unique ID", details?.unique_id], ["FS UUID", details?.fs_uuid]] : []),
              ["AI Engine", serverDisplay(
                details?.ai_engine_display,
                details?.ai_engine_name || details?.ai_engine_id,
                details?.ai_engine_ip,
              )],
              ["Telephony", serverDisplay(
                details?.telephony_server_display,
                details?.telephony_server_name,
                details?.opensips_ip,
              )],
              ["Media", serverDisplay(
                details?.media_server_display,
                details?.media_server_name,
                details?.freeswitch_ip,
              )],
              ["WebRTC", serverDisplay(
                details?.webrtc_server_display,
                details?.webrtc_server_name,
                details?.webrtc_host,
              )],
              ["PD Server", serverDisplay(
                details?.pd_server_display,
                details?.pd_server_name,
                details?.pd_server_host,
              )],
              ...(isAdmin ? [["Capacity at selection", details?.capacity_at_selection != null ? `${details.capacity_at_selection}${details.max_capacity != null ? ` / ${details.max_capacity}` : ""}` : null]] : []),
              ["Start", details?.start_time && fmt(details.start_time, details.start_time_display)],
              ["End", details?.end_time && fmt(details.end_time, details.end_time_display)],
              ["Duration", details?.duration_sec != null ? dur(details.duration_sec) : null],
              ["AI Agent dur.", details?.ai_agent_duration_sec > 0 ? dur(details.ai_agent_duration_sec) : null],
              ["AI charge", details?.ai_charge_minor > 0 ? money(details.ai_charge_minor) : null],
              ...(details?.rate_snapshot?.ai_model_price_minor != null
                ? [["AI Model per min", money(details.rate_snapshot.ai_model_price_minor)]]
                : []),
              ...((isAdmin || isReseller) && details?.rate_snapshot?.ai_billing_status
                ? [["AI billing status", details.rate_snapshot.ai_billing_status]]
                : []),
              ...(isVoicemailDropped(details)
                ? [["Voicemail Dropped", voicemailDroppedName(details)]]
                : [["Connected agent", details?.agent_name]]),
              ["Transfer result", details?.transfer_result ? transferLabel(details.transfer_result) : null],
              ["Disposition", details?.disposition ? dispLabel(systemDisposition(details, details.disposition)) : null],
              ...((isAdmin || isReseller) && details?.reseller_disposition ? [["Reseller disposition", dispLabel(details.reseller_disposition)]] : []),
              ["Hangup side", hangupSideLabel(details)],
              ["Hangup cause", details?.hangup_cause],
              ["AMD result", hasAmdLayer(details)
                ? amdResultPlain(details?.amd_result, details?.amd_action)
                : amdLabel(details?.amd_result, details?.amd_action, details?.amd_layer)],
              ["AMD cause", amdCauseLabel(details?.amd_cause)],
              ["AMD decided (ms)", details?.amd_decided_ms],
              ...(hasAmdLayer(details) ? [
                ["AMD detection", amdDetectionLabel(details?.amd_layer)],
                ["AMD Layer 1 (FreeSWITCH)", amdL1Pair(details?.amd_l1_result, details?.amd_l1_cause)],
              ] : []),
              ...(!isCustomer ? [["Transcript", details?.transcript_reference]] : []),
            ].map(([k, v]) => (
              <div key={k} data-testid={`cdr-detail-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{k}</p>
                <p className="font-mono text-xs break-all">{nn(v)}</p>
              </div>
            ))}
          </div>
          {hasAmdLayer(details) && (
            <div className="mt-1 border-t border-border pt-3" data-testid="cdr-amd-greeting">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">Initial customer greeting (speech)</p>
              {greetingState === "loading" && (
                <p className="text-xs text-muted-foreground">Loading greeting…</p>
              )}
              {greetingState === "ready" && greetingText && (
                <p className="text-sm leading-relaxed" data-testid="cdr-amd-greeting-text">“{greetingText}”</p>
              )}
              {greetingState === "none" && (
                <p className="text-xs text-muted-foreground" data-testid="cdr-amd-greeting-none">
                  No greeting sample available for this call.
                </p>
              )}
              {greetingState === "error" && (
                <p className="text-xs text-rose-600" data-testid="cdr-amd-greeting-error">
                  Couldn&apos;t load greeting (engine unreachable).
                </p>
              )}
            </div>
          )}
          {!isCustomer && details?.customer_info && Object.keys(details.customer_info).length > 0 && (
            <div className="mt-1 border-t border-border pt-3" data-testid="cdr-customer-info">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">Customer info</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {Object.entries(details.customer_info).map(([key, item]) => {
                  const row = item && typeof item === "object" ? item : { value: item };
                  return (
                    <div key={key} data-testid={`cdr-customer-info-${key}`}>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">{row.label || key}</p>
                      <p className="text-sm break-all">{row.value || "—"}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="mt-1 border-t border-border pt-3" data-testid="cdr-recording">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
              {details?.has_recording ? "Call Recording" : "Call Recording (AMD sample)"}
            </p>
            {audioState === "loading" && <p className="text-xs text-muted-foreground">Loading recording…</p>}
            {audioState === "ready" && audioUrl && (
              <audio controls preload="none" src={audioUrl} className="w-full" data-testid="cdr-audio-player" />
            )}
            {audioState === "none" && <p className="text-xs text-muted-foreground" data-testid="cdr-audio-none">No recording available for this call.</p>}
            {audioState === "error" && <p className="text-xs text-rose-600" data-testid="cdr-audio-error">Couldn't load recording (engine unreachable).</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
